import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { demoCopy, demoSteps, gallery, galleryCopy } from '../data/content';
const ease = [0.23, 1, 0.32, 1] as const;
export function Gallery() {
  const [index, setIndex] = useState(0);
  const total = gallery.length;
  const goTo = (next: number) => {
    setIndex((next + total) % total);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(index - 1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(index + 1);
    }
  };
  const current = gallery[index];
  return <section className="w-full bg-blue-900 py-14 text-white md:py-20" data-id="element-96" data-mp="2uktyat729d6e">
      <div className="mx-auto w-full max-w-5xl px-5 md:px-8" data-id="element-97" data-mp="26v81gce5va1k">
        <header className="mx-auto max-w-4xl text-center" data-id="element-98" data-mp="21yrcjfz50rnf">
          <h2 className="text-3xl font-extrabold leading-tight tracking-tight md:text-5xl" data-id="element-99" data-mp="21w55dhg5se5q">
            {demoCopy.title}
          </h2>
          <p className="mt-6 text-sm font-semibold text-blue-200" data-id="element-100" data-mp="waumxq9g9702">{demoCopy.questionLabel}</p>
          <blockquote className="mt-1 text-xl font-extrabold italic tracking-tight md:text-2xl" data-id="element-101" data-mp="111ul0yub1v45">
            «{demoCopy.question}»
          </blockquote>
        </header>

        <ol className="mx-auto mt-8 grid max-w-4xl gap-3 md:grid-cols-3 md:gap-4" data-id="element-102" data-mp="155slpdza3dzb">
          {demoSteps.map(step => <li key={step.number} className="rounded-2xl bg-white/10 p-5 ring-1 ring-white/15" data-id="element-103" data-mp="dc9x01jao7o0">
              <p className="text-sm font-extrabold uppercase tracking-widest text-blue-200" data-id="element-104" data-mp="hdvj8dhpxyav">
                {step.number} — {step.title}
              </p>
              <p className="mt-2 leading-relaxed text-white" data-id="element-105" data-mp="merigdr4kw9y">{step.text}</p>
            </li>)}
        </ol>

        <div className="mx-auto mt-14 max-w-3xl text-center" data-id="element-106" data-mp="1svwj33lvihhv">
          <h3 className="text-2xl font-extrabold tracking-tight md:text-4xl" data-id="element-107" data-mp="1xr25e3j6ty6h">
            {galleryCopy.title}
          </h3>
          <p className="mt-3 text-blue-100 md:text-lg" data-id="element-108" data-mp="3txupyh7tyyup">{galleryCopy.subtitle}</p>
        </div>

        <div role="group" aria-roledescription="carrusel" aria-label="Galería de mapas visuales" tabIndex={0} onKeyDown={handleKeyDown} className="relative mt-8 rounded-2xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-400" data-id="element-109" data-mp="3pw96ue4pdoc2">
          <div className="overflow-hidden rounded-2xl" data-id="element-110" data-mp="366780osjqy4k">
            <motion.div className="flex cursor-grab active:cursor-grabbing" animate={{
            x: `-${index * 100}%`
          }} transition={{
            duration: 0.3,
            ease
          }} drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.12} onDragEnd={(_, info) => {
            if (info.offset.x < -60) goTo(index + 1);
            if (info.offset.x > 60) goTo(index - 1);
          }} data-id="element-111" data-mp="367s0m8vaw6zs">
              {gallery.map((item, itemIndex) => <figure key={item.title} className="w-full shrink-0" aria-hidden={itemIndex !== index} data-id="element-112" data-mp="17odfy16f1spi" data-mpi={itemIndex}>
                  <img src={item.image} alt={`Mapa visual: ${item.title} — músculos, planos anatómicos y errores más comunes`} className="pointer-events-none w-full rounded-2xl bg-white" draggable={false} loading="eager" decoding="async" data-id="element-113" data-mp="tytgb484tbkf" />
                </figure>)}
            </motion.div>
          </div>

          <button type="button" onClick={() => goTo(index - 1)} aria-label="Mapa anterior" className="absolute left-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-blue-800 shadow-lg transition-colors duration-150 ease-out hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-300 md:-left-5 md:h-12 md:w-12" data-id="element-114" data-mp="1hlrwe0scahht">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-115" data-mp="gohwo17p4hqd" />
          </button>
          <button type="button" onClick={() => goTo(index + 1)} aria-label="Mapa siguiente" className="absolute right-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-blue-800 shadow-lg transition-colors duration-150 ease-out hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-300 md:-right-5 md:h-12 md:w-12" data-id="element-116" data-mp="bp59tggtrans">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-117" data-mp="qgxgl42jzgdw" />
          </button>
        </div>

        <div className="mx-auto mt-7 max-w-4xl text-center" aria-live="polite" data-id="element-118" data-mp="3g6hh9686d1ks">
          <p className="text-xl font-extrabold tracking-tight md:text-2xl" data-id="element-119" data-mp="3b5ts6q3iaut7">
            {current.title}
          </p>
          <p className="mt-2 text-blue-100" data-id="element-120" data-mp="1qpdjfhvnpehc">{current.caption}</p>
        </div>

        <div className="mt-7 flex items-center justify-center gap-3" data-id="element-121" data-mp="1vmvy7owcunx9">
          {gallery.map((item, itemIndex) => <button key={item.title} type="button" onClick={() => goTo(itemIndex)} aria-label={`Ver ${item.title}`} aria-current={itemIndex === index} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-400 ${itemIndex === index ? 'w-8 bg-white' : 'w-2.5 bg-white/35 hover:bg-white/60'}`} data-id="element-122" data-mp="1znxwo3ig6vy6" data-mpi={itemIndex} />)}
        </div>

        <p className="mt-6 text-center text-sm text-blue-200" data-id="element-123" data-mp="2nc8q4lltdtcf">
          Desliza o utiliza las flechas — {index + 1} de {total} mapas de ejemplo.
        </p>

        <div className="mx-auto mt-10 max-w-xl border-t border-white/15 pt-8 text-center" data-id="element-124" data-mp="t1by75v46g6p">
          <p className="text-xl font-extrabold tracking-tight md:text-2xl" data-id="element-125" data-mp="1wc96cw5ms1y8">{galleryCopy.footer}</p>
          <CtaButton variant="secondary" size="lg" className="mt-6 w-full" data-id="element-126" data-mp="1rdohfsvtx4p3">
            {galleryCopy.cta}
          </CtaButton>
        </div>
      </div>
    </section>;
}